import React, { useState } from 'react';
import { SearchEntity, User } from 'v2/models';
import { isSearchAssignedToMe } from 'v2/utils/searchUtils';
import ToggleButton from 'v2/common/ToggleButton/ToggleButton';
import restFactory from 'v2/utils/restFactory';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import { getKey as getSearchDetailsQueryKey } from 'v2/queries/searches/useSearchDetails';

export interface ReviewerAssignToMeButtonProps {
  // eslint-disable-next-line @typescript-eslint/ban-types
  search: SearchEntity;
  user: User;
  onComplete?: () => void;
  isLoading?: boolean;
}

const ReviewerAssignToMeButton = ({
  search,
  user,
  onComplete,
  isLoading,
}: ReviewerAssignToMeButtonProps) => {
  const [isBusy, setIsBusy] = useState(false);
  const [searchAssignedToMe, setSearchAssignedToMe] = useState(
    isSearchAssignedToMe(search, user)
  );

  const queryClient = useQueryClient();

  const toggleAssignedToMe = async (isAssignedToMe: boolean) => {
    try {
      setIsBusy(true);
      setSearchAssignedToMe(!isAssignedToMe);

      await restFactory.get<SearchEntity['id']>(
        `/dj/api/v1/search/${search.id}/assign_to_reviewer/`,
        {
          action: isAssignedToMe ? 'unassign' : 'assign',
        }
      );
    } catch (error) {
      setSearchAssignedToMe(false);
      toast.error('This request has been assigned to another reviewer');
    } finally {
      setIsBusy(false);
      queryClient.invalidateQueries(getSearchDetailsQueryKey(search.id));
      onComplete?.();
    }
  };

  if (search.assigned_to && !isSearchAssignedToMe(search, user)) {
    return null;
  }

  return (
    <ToggleButton
      className="ml-3"
      checked={searchAssignedToMe}
      onChange={() => toggleAssignedToMe(searchAssignedToMe)}
      label="Assign to me"
      ariaLabel="assignToMeBtn"
      disabled={isBusy || isLoading}
      helpText="Let your team know that you are working on this request."
    />
  );
};

export default ReviewerAssignToMeButton;
